import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { useState } from "react";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  CaseLower,
  CaseSensitive,
  CaseUpper,
  Italic,
  Strikethrough,
  Underline,
} from "lucide-react";
import {
  type BlockStyle,
  type BlockTextStyle,
  type ResponsiveStylePatch,
  type StyleDevice,
  type TextAlign,
  type TextTransform,
} from "@/lib/cms-puck/block-style";
import { CMS_PALETTE_TEXT_INPUT_CLASS } from "@/components/cms/theme/cms-color-input-styles";
import { cn } from "@/lib/utils";
import { JUSTIFY_TEXT_BLOCKS, NO_DESIGN_TEXT_ALIGN_BLOCKS } from "./constants";
import { StyleSection } from "./StyleSection";
import { ControlRow, IconToggle } from "./controls";
import { DEVICES, inheritedScalar } from "./DeviceSwitcher";

/**
 * Contrôles de traitement de texte (taille, alignement, casse, décoration), avec
 * icônes. Cliquer une option déjà active la remet à « hérite » (null). Écrit
 * `style.design.text`. La police + graisse restent gérées par le sélecteur de
 * typographie au-dessus.
 */
export function TextControls({
  style,
  onChange,
  onResponsiveChange,
  showDecoration = true,
  allowResponsive = true,
  blockType,
}: {
  style: BlockStyle;
  onChange: (patch: Partial<BlockTextStyle>) => void;
  /** Écrit une surcharge tablette/mobile (taille / lettrage / interligne / alignement). */
  onResponsiveChange: (
    device: "tablet" | "mobile",
    patch: Partial<
      Pick<
        ResponsiveStylePatch,
        "fontSize" | "letterSpacing" | "lineHeight" | "textAlign"
      >
    >,
  ) => void;
  /**
   * Affiche la rangée Gras/Italique/Souligné/Barré « niveau bloc ». `false` pour
   * le RichText (ces marques sont gérées INLINE par l'éditeur, par sélection).
   */
  showDecoration?: boolean;
  /** Bascule d'appareil (les blocs sans wrapper n'ont pas de règles générées). */
  allowResponsive?: boolean;
  blockType?: string;
}) {
  const text = style.design.text;
  const [device, setDevice] = useState<StyleDevice>("desktop");
  const cmsNew = useCmsNew();
  const aligns: Array<[TextAlign, string, React.ReactNode]> = [
    ["left", cmsNew("cms.new.fields.style.alignLeft", "Aligné à gauche"), <AlignLeft className="h-4 w-4" />],
    ["center", cmsNew("cms.new.fields.style.alignCenter", "Centré"), <AlignCenter className="h-4 w-4" />],
    ["right", cmsNew("cms.new.fields.style.alignRight", "Aligné à droite"), <AlignRight className="h-4 w-4" />],
    ["justify", cmsNew("cms.new.fields.style.alignJustify", "Justifié"), <AlignJustify className="h-4 w-4" />],
  ].filter(
    ([value]) => value !== "justify" || JUSTIFY_TEXT_BLOCKS.has(blockType ?? ""),
  ) as Array<[TextAlign, string, React.ReactNode]>;
  const transforms: Array<[TextTransform, string, React.ReactNode]> = [
    ["uppercase", cmsNew("cms.new.fields.style.transformUppercase", "Majuscules"), <CaseUpper className="h-4 w-4" />],
    ["lowercase", cmsNew("cms.new.fields.style.transformLowercase", "Minuscules"), <CaseLower className="h-4 w-4" />],
    ["capitalize", cmsNew("cms.new.fields.style.transformCapitalize", "Capitales"), <CaseSensitive className="h-4 w-4" />],
  ];

  // Vue tablette/mobile : taille, interligne, espacement des lettres et
  // alignement se surchargent. Vide = hérite du niveau supérieur.
  if (allowResponsive && device !== "desktop") {
    const patch = style.responsive[device];
    // Alignement hérité : tablette ← bureau, mobile ← tablette ← bureau.
    const inheritedAlign =
      device === "mobile"
        ? (style.responsive.tablet.textAlign ?? text.align)
        : text.align;
    const effectiveAlign = patch.textAlign ?? inheritedAlign;
    const showAlign = !NO_DESIGN_TEXT_ALIGN_BLOCKS.has(blockType ?? "");
    const inheritLabel = cmsNew("cms.new.fields.style.flow.inherit", "Hérite");
    const phFont = inheritedScalar(style, device, "fontSize");
    const phSpacing = inheritedScalar(style, device, "letterSpacing");
    const phLineHeight = inheritedScalar(style, device, "lineHeight");
    return (
      <StyleSection
        title={cmsNew("cms.new.auto.title.traitement_du_texte", "Traitement du texte")}
        description={cmsNew("cms.new.auto.description.surcharges_pour_cet_appareil_valeur_vide_heritee_du_niveau_superieur", "Surcharges pour cet appareil. Valeur vide = héritée du niveau supérieur.")}
      >
        <div className="space-y-4">
          <ControlRow label={cmsNew("cms.new.auto.label.appareil", "Appareil")}>
            {DEVICES.map(([value, key, fallback, icon]) => (
              <IconToggle
                key={value}
                label={cmsNew(key, fallback)}
                active={device === value}
                onClick={() => setDevice(value)}
              >
                {icon}
              </IconToggle>
            ))}
          </ControlRow>
          <ControlRow label={cmsNew("cms.new.auto.label.taille_px", "Taille (px)")}>
            <input
              type="number"
              min={1}
              value={patch.fontSize ?? ""}
              placeholder={phFont != null ? String(phFont) : cmsNew("cms.new.auto.placeholder.auto", "Auto")}
              className={cn(
                CMS_PALETTE_TEXT_INPUT_CLASS,
                "h-9 w-24 text-center tabular-nums",
              )}
              onChange={(e) => {
                const raw = e.target.value;
                const n = raw === "" ? null : Number(raw);
                onResponsiveChange(device, {
                  fontSize: n != null && Number.isFinite(n) && n > 0 ? n : null,
                });
              }}
            />
          </ControlRow>
          <ControlRow label={cmsNew("cms.new.auto.label.interligne", "Interligne")}>
            <input
              type="number"
              min={0.5}
              step={0.1}
              value={patch.lineHeight ?? ""}
              placeholder={phLineHeight != null ? String(phLineHeight) : cmsNew("cms.new.auto.placeholder.auto", "Auto")}
              className={cn(
                CMS_PALETTE_TEXT_INPUT_CLASS,
                "h-9 w-24 text-center tabular-nums",
              )}
              onChange={(e) => {
                const raw = e.target.value;
                const n = raw === "" ? null : Number(raw);
                onResponsiveChange(device, {
                  lineHeight:
                    n != null && Number.isFinite(n) && n > 0 ? n : null,
                });
              }}
            />
          </ControlRow>
          <ControlRow label={cmsNew("cms.new.auto.label.espacement_px", "Espacement (px)")}>
            <input
              type="number"
              step={0.5}
              value={patch.letterSpacing ?? ""}
              placeholder={phSpacing != null ? String(phSpacing) : cmsNew("cms.new.auto.placeholder.auto", "Auto")}
              className={cn(
                CMS_PALETTE_TEXT_INPUT_CLASS,
                "h-9 w-24 text-center tabular-nums",
              )}
              onChange={(e) => {
                const raw = e.target.value;
                const n = raw === "" ? null : Number(raw);
                onResponsiveChange(device, {
                  letterSpacing: n != null && Number.isFinite(n) ? n : null,
                });
              }}
            />
          </ControlRow>
          {showAlign && text.align != null ? (
            <ControlRow label={cmsNew("cms.new.auto.label.alignement", "Alignement")}>
              {aligns.map(([value, label, icon]) => (
                <IconToggle
                  key={value}
                  label={
                    patch.textAlign == null && effectiveAlign === value
                      ? `${label} (${inheritLabel})`
                      : label
                  }
                  active={effectiveAlign === value}
                  inherited={patch.textAlign == null}
                  onClick={() =>
                    onResponsiveChange(device, {
                      textAlign: patch.textAlign === value ? null : value,
                    })
                  }
                >
                  {icon}
                </IconToggle>
              ))}
            </ControlRow>
          ) : null}
          {showAlign && text.align == null ? (
            <p className="text-xs leading-relaxed text-slate-400">
              {cmsNew(
                "cms.new.fields.style.alignResponsiveNeedsDesktop",
                "Choisissez d'abord un alignement en vue Bureau pour l'adapter à cet appareil.",
              )}
            </p>
          ) : null}
          <p className="text-xs leading-relaxed text-slate-400">
            {cmsNew(
              "cms.new.fields.style.textResponsiveHintCase",
              "Casse et décoration se règlent en vue Bureau.",
            )}
          </p>
        </div>
      </StyleSection>
    );
  }

  return (
    <StyleSection
      title={cmsNew("cms.new.auto.title.traitement_du_texte", "Traitement du texte")}
      description={cmsNew("cms.new.auto.description.taille_alignement_casse_et_decoration_appliques_au_texte_du_bloc", "Taille, alignement, casse et décoration appliqués au texte du bloc.")}
    >
      <div className="space-y-4">
        {allowResponsive ? (
          <ControlRow label={cmsNew("cms.new.auto.label.appareil", "Appareil")}>
            {DEVICES.map(([value, key, fallback, icon]) => (
              <IconToggle
                key={value}
                label={cmsNew(key, fallback)}
                active={device === value}
                onClick={() => setDevice(value)}
              >
                {icon}
              </IconToggle>
            ))}
          </ControlRow>
        ) : null}

        <ControlRow label={cmsNew("cms.new.auto.label.taille_px", "Taille (px)")}>
          <input
            type="number"
            min={1}
            value={text.fontSize ?? ""}
            placeholder={cmsNew("cms.new.auto.placeholder.auto", "Auto")}
            className={cn(
              CMS_PALETTE_TEXT_INPUT_CLASS,
              "h-9 w-24 text-center tabular-nums",
            )}
            onChange={(e) => {
              const raw = e.target.value;
              const n = raw === "" ? null : Number(raw);
              onChange({
                fontSize: n != null && Number.isFinite(n) && n > 0 ? n : null,
              });
            }}
          />
        </ControlRow>

        {!NO_DESIGN_TEXT_ALIGN_BLOCKS.has(blockType ?? "") ? (
          <ControlRow label={cmsNew("cms.new.auto.label.alignement", "Alignement")}>
            {aligns.map(([value, label, icon]) => (
              <IconToggle
                key={value}
                label={label}
                active={text.align === value}
                onClick={() =>
                  onChange({ align: text.align === value ? null : value })
                }
              >
                {icon}
              </IconToggle>
            ))}
          </ControlRow>
        ) : null}

        {showDecoration ? (
          <ControlRow label={cmsNew("cms.new.auto.label.style", "Style")}>
            <IconToggle
              label={cmsNew("cms.new.auto.label.gras", "Gras")}
              active={text.bold}
              onClick={() => onChange({ bold: !text.bold })}
            >
              <Bold className="h-4 w-4" />
            </IconToggle>
            <IconToggle
              label={cmsNew("cms.new.auto.label.italique", "Italique")}
              active={text.italic}
              onClick={() => onChange({ italic: !text.italic })}
            >
              <Italic className="h-4 w-4" />
            </IconToggle>
            <IconToggle
              label={cmsNew("cms.new.auto.label.souligne", "Souligné")}
              active={text.underline}
              onClick={() => onChange({ underline: !text.underline })}
            >
              <Underline className="h-4 w-4" />
            </IconToggle>
            <IconToggle
              label={cmsNew("cms.new.auto.label.barre", "Barré")}
              active={text.strikethrough}
              onClick={() => onChange({ strikethrough: !text.strikethrough })}
            >
              <Strikethrough className="h-4 w-4" />
            </IconToggle>
          </ControlRow>
        ) : null}

        <ControlRow label={cmsNew("cms.new.auto.label.casse", "Casse")}>
          {transforms.map(([value, label, icon]) => (
            <IconToggle
              key={value}
              label={label}
              active={text.transform === value}
              onClick={() =>
                onChange({ transform: text.transform === value ? null : value })
              }
            >
              {icon}
            </IconToggle>
          ))}
        </ControlRow>

        <ControlRow label={cmsNew("cms.new.auto.label.interligne", "Interligne")}>
          <input
            type="number"
            min={0.5}
            step={0.1}
            value={text.lineHeight ?? ""}
            placeholder={cmsNew("cms.new.auto.placeholder.auto", "Auto")}
            className={cn(
              CMS_PALETTE_TEXT_INPUT_CLASS,
              "h-9 w-24 text-center tabular-nums",
            )}
            onChange={(e) => {
              const raw = e.target.value;
              const n = raw === "" ? null : Number(raw);
              onChange({
                lineHeight: n != null && Number.isFinite(n) && n > 0 ? n : null,
              });
            }}
          />
        </ControlRow>

        <ControlRow label={cmsNew("cms.new.auto.label.espacement_px", "Espacement (px)")}>
          <input
            type="number"
            step={0.5}
            value={text.letterSpacing ?? ""}
            placeholder={cmsNew("cms.new.auto.placeholder.auto", "Auto")}
            className={cn(
              CMS_PALETTE_TEXT_INPUT_CLASS,
              "h-9 w-24 text-center tabular-nums",
            )}
            onChange={(e) => {
              const raw = e.target.value;
              const n = raw === "" ? null : Number(raw);
              onChange({
                letterSpacing: n != null && Number.isFinite(n) ? n : null,
              });
            }}
          />
        </ControlRow>
      </div>
    </StyleSection>
  );
}
